/**
 * Clients API
 * CRUD operations for client management
 */

import apiClient from '@/lib/api-client'
import type {
  Client,
  ClientCreate,
  ClientUpdate,
  ClientFilters,
  ClientListItem,
} from '@/types/client'
import type { PaginatedResponse, PaginationParams } from '@/types/common'

export const clientsApi = {
  /**
   * Get paginated list of clients
   */
  async getAll(
    filters?: ClientFilters,
    pagination?: PaginationParams
  ): Promise<PaginatedResponse<ClientListItem>> {
    const params = { ...filters, ...pagination }
    const response = await apiClient.get<PaginatedResponse<ClientListItem>>(
      '/clients',
      { params }
    )
    return response.data
  },

  /**
   * Get client by ID
   */
  async getById(id: string): Promise<Client> {
    const response = await apiClient.get<Client>(`/clients/${id}`)
    return response.data
  },

  /**
   * Create new client
   */
  async create(data: ClientCreate): Promise<Client> {
    const response = await apiClient.post<Client>('/clients', data)
    return response.data
  },

  /**
   * Update existing client
   */
  async update(id: string, data: ClientUpdate): Promise<Client> {
    const response = await apiClient.put<Client>(`/clients/${id}`, data)
    return response.data
  },

  /**
   * Delete client (soft delete)
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/clients/${id}`)
  },

  /**
   * Get client's cases
   */
  async getCases(id: string): Promise<any[]> {
    const response = await apiClient.get(`/clients/${id}/cases`)
    return response.data
  },

  /**
   * Get client's visits
   */
  async getVisits(id: string): Promise<any[]> {
    const response = await apiClient.get(`/clients/${id}/visits`)
    return response.data
  },
}

export default clientsApi
